import {
  makeStyles,
  tokens,
  Input,
  Label,
  Text,
  Spinner,
} from '@coreai/fluentui-react';
import { CopyIconButton } from '@/components/shared';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  label: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
  },
  required: {
    color: tokens.colorPaletteRedForeground1,
    marginLeft: tokens.spacingHorizontalXXS,
  },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  input: {
    flex: 1,
    minWidth: 0,
    // Code-chip treatment for read-only generated values.
    // Subtle muted fill (vs. the bright fill of editable inputs) signals
    // "this is a displayed value", not "type here".
    backgroundColor: tokens.colorNeutralBackground2,
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
    // Target the inner Fluent <input> to remove the edit caret. Focus and
    // text selection still work — users can click + ⌘A to copy. Font stays
    // consistent with the rest of the parameters panel (no monospace) so
    // the field doesn't visually break with neighboring inputs.
    '& input': {
      caretColor: 'transparent',
      // Text cursor (not edit cursor) — selection is still expected.
      cursor: 'text',
      color: tokens.colorNeutralForeground2,
    },
    '@media (forced-colors: active)': {
      backgroundColor: 'ButtonFace',
      borderTopColor: 'GrayText',
      borderRightColor: 'GrayText',
      borderBottomColor: 'GrayText',
      borderLeftColor: 'GrayText',
      '& input': {
        color: 'GrayText',
      },
    },
  },
  emptyState: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    minHeight: '32px',
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackgroundDisabled,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
    fontStyle: 'italic',
    userSelect: 'none',
    '@media (forced-colors: active)': {
      color: 'GrayText',
      borderTopColor: 'GrayText',
      borderRightColor: 'GrayText',
      borderBottomColor: 'GrayText',
      borderLeftColor: 'GrayText',
    },
  },
  description: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  error: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },
});

export interface CopyableFieldProps {
  name: string;
  label: string;
  value: string;
  description?: string | undefined;
  placeholder?: string | undefined;
  required?: boolean | undefined;
  error?: string | undefined;
  /** When true, shows a loading spinner instead of the copy button. */
  loading?: boolean | undefined;
}

/**
 * CopyableField - Read-only text field with a copy-to-clipboard button.
 * Used for runtime-generated values (e.g. trigger callback URLs) that the
 * user cannot edit but typically wants to copy.
 */
export const CopyableField = ({
  name,
  label,
  value,
  description,
  placeholder,
  required,
  error,
  loading,
}: CopyableFieldProps) => {
  const styles = useStyles();
  const hasValue = !!value;

  return (
    <div className={styles.container}>
      <Label className={styles.label} htmlFor={name} id={`${name}-label`}>
        {label}
        {required && <span className={styles.required}>*</span>}
      </Label>
      <div className={styles.inputRow}>
        {hasValue ? (
          <Input
            id={name}
            className={styles.input}
            value={value}
            readOnly
            placeholder={placeholder}
            title={value}
            aria-describedby={description ? `${name}-description` : undefined}
          />
        ) : (
          // The empty-state placeholder is rendered as a non-interactive
          // textbox: `role="textbox"` makes `aria-readonly` semantically
          // valid (it has no defined meaning on a generic <div>), and
          // `aria-labelledby` carries the label association since
          // <Label htmlFor> only works against real form controls.
          // `aria-multiline="false"` matches the single-line shape of
          // the populated <Input>.
          <div
            className={styles.emptyState}
            role="textbox"
            aria-readonly="true"
            aria-multiline="false"
            aria-labelledby={`${name}-label`}
            aria-describedby={description ? `${name}-description` : undefined}
            aria-busy={loading || undefined}
          >
            {placeholder ?? 'Value will be generated after save'}
          </div>
        )}
        {loading ? (
          <Spinner size="tiny" aria-label={`Loading ${label}`} />
        ) : hasValue ? (
          <CopyIconButton value={value} label={label} />
        ) : null}
      </div>
      {description && (
        <Text id={`${name}-description`} className={styles.description}>
          {description}
        </Text>
      )}
      {error && <Text className={styles.error}>{error}</Text>}
    </div>
  );
};
